import { DateTime } from 'luxon';
import { TranslationService } from '../services/translation.service';

export class DateHelper {
  public static DATE_FORMAT = 'yyyy-MM-dd';
  public static TIME_FORMAT = 'HH:mm';

  public static getDateFromDateTimeString(dateTimeString: string): string {
    if (!dateTimeString) {
      return '';
    }
    let dateTime = DateTime.fromISO(dateTimeString);

    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(dateTimeString, 'yyyy-MM-dd HH:mm');
    }

    if (!dateTime.isValid) {
      return dateTimeString;
    }

    return dateTime.toFormat(DateHelper.DATE_FORMAT);
  }

  public static getTimeFromDateTimeString(dateTimeString: string | undefined): string {
    if (!dateTimeString) {
      return '';
    }
    let dateTime = DateTime.fromISO(dateTimeString);

    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(dateTimeString, 'yyyy-MM-dd HH:mm');
    }

    if (!dateTime.isValid) {
      return dateTimeString;
    }

    return dateTime.toFormat(DateHelper.TIME_FORMAT);
  }

  // In minutes
  public static getDurationFromDateStrings(fromDateString: string, toDateString: string): number {
    const from = DateTime.fromISO(fromDateString);
    const to = DateTime.fromISO(toDateString);

    return this.getDurationInMinutes(from, to);
  }

  public static getDurationInMinutes(from: DateTime, to: DateTime): number {
    const durationInMinutes = to.diff(from, ['minutes']).toObject().minutes;
    if (!durationInMinutes) return 0;
    return Math.round(durationInMinutes);
  }

  public static formatAsHoursAndMinutes(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    let minutes = value;
    const negativeValue = minutes < 0;
    if (negativeValue) {
      minutes = Math.abs(minutes);
    }

    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) {
      return negativeValue ? `-${h} h ${m} min` : `${h} h ${m} min`;
    }
    return negativeValue ? `-${m} min` : `${m} min`;
  }

  public static format(dateTimeString: string, format: string) {
    const outputDate = DateTime.fromISO(dateTimeString);
    return outputDate.toFormat(format);
  }

  public static compareDate(date1: DateTime, date2: DateTime): number {
    const d1 = date1.startOf('day');
    const d2 = date2.startOf('day');

    if (d1 > d2) {
      return 1;
    }
    if (d2 > d1) {
      return -1;
    }
    return 0;
  }

  public static getTrafficDate(inputDate: string): string {
    let date = DateTime.fromISO(inputDate);
    if (date.hour < 4) {
      date = date.minus({ days: 1 });
    }
    return date.toFormat(DateHelper.DATE_FORMAT);
  }

  public static isSameDay(date1: DateTime, date2: DateTime): boolean {
    if (!date1 || !date2) {
      return false;
    }
    return date1.hasSame(date2, 'day');
  }

  public static formatWithWeekday(value: DateTime): string {
    const today = DateTime.now().startOf('day');
    const yesterday = today.minus({ days: 1 });
    const tomorrow = today.plus({ days: 1 });

    let weekDay = 'EEEE';
    if (value.hasSame(today, 'day')) {
      weekDay = `'${TranslationService.get('common.today')}'`;
    }
    if (value.hasSame(yesterday, 'day')) {
      weekDay = `'${TranslationService.get('common.yesterday')}'`;
    }
    if (value.hasSame(tomorrow, 'day')) {
      weekDay = `'${TranslationService.get('common.tomorrow')}'`;
    }

    const dateString = value.toFormat(`${weekDay} d MMMM yyyy`);

    return dateString.charAt(0).toUpperCase() + dateString.slice(1);
  }

  public static parseWithFallback(dateString: string | undefined, fallback: DateTime | undefined): DateTime | undefined {
    if (!dateString) return fallback;
    const date = DateTime.fromISO(dateString);
    return date.isValid ? date : fallback;
  }

  public static roundToNearestFiveMinutes(dateTime: DateTime): DateTime {
    return dateTime.set({ minute: Math.round(dateTime.minute / 5) * 5, second: 0, millisecond: 0 });
  }
}
